import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  EntityIdentifier,
  Geofence,
  GeofenceEntity,
  geofenceSelector, geofenceTriggerTypes, geofenceTypes,
  useGetGeofenceQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  GeofenceForm,
  GeofenceFormDefaultValues,
  GeofenceFormValues
} from "../../components/form/GeofenceForm/GeofenceForm";
import {useGeofenceMutation} from "../../hooks/useGeofenceMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: GeofenceFormDefaultValues
}

export const GeofenceFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <GeofenceFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromGeofence = (geofence: GeofenceEntity | undefined): GeofenceFormDefaultValues => {
  let object: EntityIdentifier | undefined = undefined

  if (geofence && geofence.objectId && geofence.objectType) {
    object = {
      id: geofence.objectId,
      type: geofence.objectType,
    }
  }

  return {
    triggerType: geofence?.triggerType,
    latCoord: geofence?.latCoord,
    longCoord: geofence?.longCoord,
    radius: geofence?.radius,
    object: object,
  };
};

export const GeofenceFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const {regionId, googleMapsApiKey} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectGeofence = useMemo(() => {
    return geofenceSelector()
  }, [])

  const {currGeofence, isLoading} = useGetGeofenceQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currGeofence: selectGeofence(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: GeofenceFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromGeofence(currGeofence);
  }, [currGeofence, formDefaults]);

  const isEdit = useMemo(() => {
    return currGeofence && !copy
  }, [currGeofence, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.geofence.form.title.edit", "Edit Geofence", {ns: "common"});
    } else {
      return t("page.entity.geofence.form.title.new", "New Geofence", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createGeofence,
    updateGeofence,
    isCreating,
    isUpdating,
  } = useGeofenceMutation()

  const onGeofenceSubmit = useCallback((formValues: GeofenceFormValues) => {
    const patch: Partial<Geofence> = {
      type: geofenceTypes.circular.code, // TODO: this is defaulted for now until we support polygon
      triggerType: formValues.triggerType.code,
      latCoord: formValues.latCoord,
      longCoord: formValues.longCoord,
      radius: formValues.radius,
      objectId: formValues.object?.id,
      objectType: formValues.object?.type,
    }

    if (isEdit) {
      const geofence: Partial<Geofence> = {
        ...currGeofence.asTypeValue(),
        ...patch,
      };
      updateGeofence(geofence, {onSuccess: () => navigate(-1)})
    } else {
      const geofence: Partial<Geofence> = {
        ...patch,
      };
      createGeofence(geofence, {onSuccess: () => navigate(-1)})
    }
  }, [currGeofence, isEdit, createGeofence, updateGeofence, navigate]);

  return (
    <ProtectedPage
      entity={currGeofence}
      permissions={[isEdit ? "geofence_edit" : "geofence_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <GeofenceForm
                  options={{
                    regionId: regionId,
                    objectSelectDisabled: typeof currGeofence !== 'undefined' || typeof formDefaults?.object !== 'undefined',
                    googleMapsApiKey: googleMapsApiKey,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onGeofenceSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

